import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import ContinueButton from "./ContinueButton";
import {
  submitPersonalInfo, savePersonalInfoToStore } from '../store/personalInfoSlice'

const PersonalInfoForm = ({ onNext }) => {
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.personalInfoFormData.personalInfo);
  const email = useSelector((state) => state.auth.email);
  const userId = useSelector((state) => state.auth.id);
  const { role } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    profilePicture: userInfo?.profilePicture ||  null,
    firstName: userInfo?.firstName || "",
    lastName: userInfo?.lastName || "",
    country: userInfo?.country || "",
    stateProvince: userInfo?.stateProvince || "",
    city: userInfo?.city || "",
    personalDetails: userInfo?.personalDetails || "",
    phone: userInfo?.phone || "",
  });

  const [imagePreview, setImagePreview] = useState("/default-profile.png");

  useEffect(() => {
    if (formData.profilePicture instanceof File) {
      const previewUrl = URL.createObjectURL(formData.profilePicture);
      setImagePreview(previewUrl);

      return () => URL.revokeObjectURL(previewUrl);
    } else if (
      typeof formData.profilePicture === "string" &&
      formData.profilePicture.trim() !== ""
    ) {
      setImagePreview(formData.profilePicture);
    } else {
      setImagePreview("/default-profile.png");
    }
  }, [formData.profilePicture]);


  const isDisabled =
    !formData.firstName ||
    !formData.lastName ||
    !formData.country ||
    !formData.stateProvince ||
    !formData.city;

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "personalDetails" && value.length > 250) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, selectedOption) => {
    setFormData((prev) => ({ ...prev, [name]: selectedOption?.value || "" }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        profilePicture: file,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const hasChanged = Object.keys(formData).some(
      (key) => formData[key] !== userInfo?.[key]
    );
    if (!hasChanged) {
      onNext();
      return;
    }

    const userData = {
      profilePicture: formData.profilePicture,
      firstName: formData.firstName,
      lastName: formData.lastName,
      country: formData.country,
      stateProvince: formData.stateProvince,
      city: formData.city,
      personalDetails: formData.personalDetails,
      phone: formData.phone,
      email: email,
      createdBy: userId,
      accountType: role

    };

    await dispatch(submitPersonalInfo(userData));
    dispatch(savePersonalInfoToStore(formData));
    onNext();
  };

  const countries = [
    { label: "United States", value: "United States" },
    { label: "Canada", value: "Canada" },
  ];

  const statesProvinces = {
    "United States": [
      "Alabama",
      "Alaska",
      "Arizona",
      "Arkansas",
      "California",
      "Colorado",
      "Connecticut",
      "Delaware",
      "Florida",
      "Georgia",
      "Hawaii",
      "Idaho",
      "Illinois",
      "Indiana",
      "Iowa",
      "Kansas",
      "Kentucky",
      "Louisiana",
      "Maine",
      "Maryland",
      "Massachusetts",
      "Michigan",
      "Minnesota",
      "Mississippi",
      "Missouri",
      "Montana",
      "Nebraska",
      "Nevada",
      "New Hampshire",
      "New Jersey",
      "New Mexico",
      "New York",
      "North Carolina",
      "North Dakota",
      "Ohio",
      "Oklahoma",
      "Oregon",
      "Pennsylvania",
      "Rhode Island",
      "South Carolina",
      "South Dakota",
      "Tennessee",
      "Texas",
      "Utah",
      "Vermont",
      "Virginia",
      "Washington",
      "West Virginia",
      "Wisconsin",
      "Wyoming",
    ],
    Canada: [
      "Alberta",
      "British Columbia",
      "Manitoba",
      "New Brunswick",
      "Newfoundland and Labrador",
      "Northwest Territories",
      "Nova Scotia",
      "Nunavut",
      "Ontario",
      "Prince Edward Island",
      "Quebec",
      "Saskatchewan",
      "Yukon",
    ],
  };

  const customStyles = {
    menu: (provided) => ({
      ...provided,
      textAlign: "start",
      background: "#F7F7F7",
      borderRadius: "0.5rem",
    }),
    control: (provided, state) => ({
      ...provided,
      border: "none",
      background: "#F9F9F9",
      textAlign: "start",
      height: "48px",
      borderRadius: "0.5rem",
      boxShadow: state.isFocused
        ? "0 0 0 2px rgba(128, 128, 128, 0.5)"
        : provided.boxShadow,
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#1D2639",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "lightgray"
        : state.isFocused
        ? "lightgray"
        : provided.backgroundColor,
      color: "black",
      padding: "6px",
      borderRadius: "0.5rem",
      "&:active": {
        backgroundColor: "lightgray",
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#B0B0B0",
    }),
  };

  return (
    <section className="flex flex-col min-h-screen justify-start items-center py-10 px-6">
      <header className="mb-14">
        <h3 className="text-xl font-medium text-[#7E7E7E] text-center">
          Step 1
        </h3>
        <h1 className="text-2xl font-semibold text-[#1D2639] text-center">
          Personal Information
        </h1>
        <p className="text-base font-medium text-[#7E7E7E] text-center mt-2">
          Fill in your details to create your profile.
        </p>
      </header>
      <form className="flex flex-col w-full sm:w-[572px]">
        <fieldset className="mb-8">
          <label className="block text-[#1D2639] text-[12px] mb-2 text-start font-normal">
            Profile Picture
          </label>
          <div className="flex flex-col justify-start items-start space-y-4">
          <img
              src={imagePreview}
              alt=""
              className="w-24 h-24 rounded-full bg-gray-200 object-cover"
            />
            <label
              htmlFor="fileUpload"
              className="cursor-pointer flex items-center space-x-2 border border-gray-300 px-4 py-2 rounded-lg text-[#1D2639]"
            >
              {/* Placeholder for Custom SVG */}
              <div className="w-5 h-5 text-[#1D2639]">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M3 21C2.45 21 1.97917 20.8042 1.5875 20.4125C1.19583 20.0208 1 19.55 1 19V7C1 6.45 1.19583 5.97917 1.5875 5.5875C1.97917 5.19583 2.45 5 3 5H6.15L8 3H14V5H8.875L7.05 7H3V19H19V10H21V19C21 19.55 20.8042 20.0208 20.4125 20.4125C20.0208 20.8042 19.55 21 19 21H3ZM19 7V5H17V3H19V1H21V3H23V5H21V7H19ZM11 17.5C12.25 17.5 13.3125 17.0625 14.1875 16.1875C15.0625 15.3125 15.5 14.25 15.5 13C15.5 11.75 15.0625 10.6875 14.1875 9.8125C13.3125 8.9375 12.25 8.5 11 8.5C9.75 8.5 8.6875 8.9375 7.8125 9.8125C6.9375 10.6875 6.5 11.75 6.5 13C6.5 14.25 6.9375 15.3125 7.8125 16.1875C8.6875 17.0625 9.75 17.5 11 17.5ZM11 15.5C10.3 15.5 9.70833 15.2583 9.225 14.775C8.74167 14.2917 8.5 13.7 8.5 13C8.5 12.3 8.74167 11.7083 9.225 11.225C9.70833 10.7417 10.3 10.5 11 10.5C11.7 10.5 12.2917 10.7417 12.775 11.225C13.2583 11.7083 13.5 12.3 13.5 13C13.5 13.7 13.2583 14.2917 12.775 14.775C12.2917 15.2583 11.7 15.5 11 15.5Z"
                    fill="#1D2739"
                  />
                </svg>
              </div>
              <span className="text-base text-[#1D2739] font-medium">
              Pick a profile picture
              </span>
            </label>
            <input
              id="fileUpload"
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
          </div>
        </fieldset>

        {[
          {
            label: "First Name",
            name: "firstName",
            type: "text",
            required: true,
          },
          {
            label: "Last Name",
            name: "lastName",
            type: "text",
            required: true,
          },
        ].map(({ label, name, type, required }) => (
          <fieldset key={name} className="mb-8">
            <label className="block text-[#1D2639] text-[12px] mb-2 text-start font-normal">
              {label} <span className="text-red-500">*</span>
            </label>
            <input
              type={type}
              id={name}
              name={name}
              placeholder={`Enter ${label}`}
              className="w-full h-12 p-3 bg-[#F9F9F9] rounded-lg border-2 border-gray-300"
              value={formData[name]}
              onChange={handleChange}
              required={required}
            />
          </fieldset>
        ))}
        <fieldset className="mb-8">
          <label className="block text-[#1D2639] text-[12px] mb-2 text-start font-normal">
            Country <span className="text-red-500">*</span>
          </label>
          <Select
            options={countries}
            styles={customStyles}
            value={countries.find((c) => c.value === formData.country)}
            onChange={(selectedOption) =>
              handleSelectChange("country", selectedOption)
            }
            placeholder="Select Country"
          />
        </fieldset>
        {formData.country && (
          <fieldset className="mb-8">
            <label className="block text-[#1D2639] text-[12px] mb-2 text-start font-normal">
              State/Province <span className="text-red-500">*</span>
            </label>
            <Select
              styles={customStyles}
              placeholder="Select State/Province"
              options={
                formData.country && statesProvinces[formData.country]
                  ? statesProvinces[formData.country].map((state) => ({
                      label: state,
                      value: state,
                    }))
                  : []
              }
              value={
                formData.stateProvince
                  ? {
                      label: formData.stateProvince,
                      value: formData.stateProvince,
                    }
                  : null
              }
              onChange={(selectedOption) =>
                handleSelectChange("stateProvince", selectedOption)
              }
            />
          </fieldset>
        )}
        <fieldset className="mb-8">
          <label className="block text-[#1D2639] text-[12px] mb-2 text-start font-normal">
            City <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="city"
            placeholder="Enter City"
            className="w-full h-12 p-3 bg-[#F9F9F9] rounded-lg border-2 border-gray-300"
            value={formData.city}
            onChange={handleChange}
            required
          />
        </fieldset>
        <fieldset className="mb-8">
          <label className="block text-[#1D2639] text-[12px] mb-2 text-start font-normal">
            Personal Details
          </label>
          <textarea
            name="personalDetails"
            maxLength={250}
            placeholder="Enter personal details (max 250 characters)"
            className="w-full h-24 p-3 bg-[#F9F9F9] rounded-lg border-2 border-gray-300 resize-none"
            value={formData.personalDetails}
            onChange={handleChange}
          />
        </fieldset>
        {[
          {
            label: "Phone Number",
            name: "phone",
            type: "text",
            placeholder: "XXX-XXX-XXXX",
            required: true,
          }
        ].map(({ label, name, type, placeholder, required }) => (
          <fieldset key={name} className="mb-8">
            <label className="block text-[#1D2639] text-[12px] mb-2 text-start font-normal">
              {label} <span className="text-red-500">*</span>
            </label>
            <input
              type={type}
              id={name}
              name={name}
              placeholder={placeholder}
              className="w-full h-12 p-3 bg-[#F9F9F9] rounded-lg border-2 border-gray-300"
              value={formData[name]}
              onChange={handleChange}
              required={required}
            />
          </fieldset>
        ))}
        <ContinueButton
          onClick={handleSubmit}
          label="Save and Continue"
          isDisabled={isDisabled}
        />
      </form>
    </section>
  );
};

export default PersonalInfoForm;